import React from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, X } from "lucide-react";

/**
 * ConfirmModal — viewport-centered confirm dialog.
 *
 * IMPORTANT: rendered via `createPortal` to `document.body` so it ALWAYS
 * centers in the viewport, regardless of where the caller placed
 * <ConfirmModal/> in its tree.
 *
 * Why this matters: the Navbar's profile dropdown contains a
 * `transform`/`overflow-hidden` chain (it animates in/out). When this
 * modal was a plain child of the dropdown, `position: fixed` was scoped
 * to the dropdown's transformed container (CSS spec: any ancestor with
 * `transform` or `will-change` becomes the containing block for `fixed`).
 * That's why the user saw the modal stuck to the top of the screen
 * partially cut off, instead of centered.
 *
 * Portaling to body sidesteps that entirely.
 */
const ConfirmModal = ({
  title = "Are you sure?",
  message = "This action cannot be undone.",
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  variant = "danger", // danger | warning | info
  onConfirm,
  onCancel,
  loading = false,
}) => {
  const variants = {
    danger: {
      icon: "bg-red-500/15 text-red-400",
      btn: "bg-red-500 hover:bg-red-400 text-white",
      border: "border-red-500/20",
    },
    warning: {
      icon: "bg-amber-500/15 text-amber-400",
      btn: "bg-amber-500 hover:bg-amber-400 text-black",
      border: "border-amber-500/20",
    },
    info: {
      icon: "bg-orange-500/15 text-amber-400",
      btn: "bg-orange-500 hover:bg-amber-400 text-white",
      border: "border-orange-500/20",
    },
  };

  const v = variants[variant] || variants.danger;

  const modal = (
    <div
      className="fixed inset-0 z-[99999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="confirm-modal"
      onClick={(e) => { if (e.target === e.currentTarget && onCancel) onCancel(); }}
    >
      <div className={`bg-[#111] border ${v.border} rounded-2xl w-full max-w-sm p-6 shadow-2xl`}>
        <div className="flex items-start gap-4">
          <div className={`w-11 h-11 rounded-xl ${v.icon} flex items-center justify-center flex-shrink-0`}>
            <AlertTriangle size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-white font-bold text-base mb-1">{title}</h3>
            <p className="text-gray-400 text-sm leading-relaxed">{message}</p>
          </div>
          <button onClick={onCancel} className="text-gray-600 hover:text-gray-400 flex-shrink-0" data-testid="confirm-modal-cancel-x">
            <X size={18} />
          </button>
        </div>
        <div className="flex gap-3 mt-6">
          <button
            onClick={onCancel}
            className="flex-1 bg-white/[0.06] border border-white/10 text-gray-300 rounded-xl px-4 py-2.5 text-sm font-medium hover:bg-white/[0.1] transition-all active:scale-[0.97]"
            data-testid="confirm-modal-cancel"
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`flex-1 ${v.btn} rounded-xl px-4 py-2.5 text-sm font-bold transition-all active:scale-[0.97] disabled:opacity-50`}
            data-testid="confirm-modal-confirm"
          >
            {loading ? "..." : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );

  // SSR safety: createPortal requires document. Only mount client-side.
  if (typeof document === "undefined") return null;
  return createPortal(modal, document.body);
};

export default ConfirmModal;
